import { pushAddedToCart, type AddToCartResponse } from '@/composables/cart/useAddToCartTracking';
import { useAlgoliaHeaderSearchStore } from '@/stores/search/AlgoliaHeaderSearchStore';
import CartAddSetController from '@/types/wayfinder/actions/App/Http/Controllers/Api/Cart/CartAddSetController';
import CartPageController from '@/types/wayfinder/actions/App/Http/Controllers/Web/Cart/CartPageController';
import { router, useHttp } from '@inertiajs/vue3';
import { ref } from 'vue';

export type SetSelection = {
    position: number;
    sku: string;
    ean: string | null;
};

type AddSetToCartRequestData = {
    selections: SetSelection[];
    quantity: number;
    magento_sku: string;
    algolia_query_id: string | null;
};

/**
 * Adds a set to the cart: one ordinary line per position, sharing the set's
 * discount.
 */
export const useAddSetToCart = () => {
    const algoliaHeaderSearchStore = useAlgoliaHeaderSearchStore();

    const isLoading = ref(false);
    const cartError = ref(false);
    const addHttp = useHttp<AddSetToCartRequestData, AddToCartResponse>();

    function addSetToCart(magentoSku: string, selections: SetSelection[], quantity = 1): void {
        isLoading.value = true;
        cartError.value = false;

        addHttp.transform((): AddSetToCartRequestData => buildPayload(magentoSku, selections, quantity));

        void addHttp
            .post(CartAddSetController.url(), {
                onSuccess: handleSuccess,
                // Never retried: a failure on a later line leaves the earlier lines in the cart.
                onError: fail,
                onHttpException: fail,
                onNetworkError: fail,
                onCancel: (): void => {
                    isLoading.value = false;
                },
            })
            .catch((): void => {
                // Handled by the callbacks above; the promise also rejects on failure.
            });
    }

    function buildPayload(magentoSku: string, selections: SetSelection[], quantity: number): AddSetToCartRequestData {
        return {
            // Ordered, and a repeated SKU across positions is meaningful, so
            // this must not be deduplicated or sorted anywhere on the way out.
            selections,
            quantity,
            magento_sku: magentoSku,
            algolia_query_id: algoliaHeaderSearchStore.latestQueryId ?? null,
        };
    }

    function handleSuccess(response: AddToCartResponse): void {
        // Pushed on the page where the set was added, before moving on to the cart.
        pushAddedToCart(response.items ?? []);

        router.visit(CartPageController.url(), {
            onFinish: (): void => {
                isLoading.value = false;
            },
        });
    }

    function fail(): void {
        cartError.value = true;
        isLoading.value = false;
    }

    return { addSetToCart, isLoading, cartError };
};
